import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Params, Router, UrlTree} from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {MpopFeatureModel} from './model/mpop-feature.model';
import {UserService} from './service/user.service';

const explicitSaveOfferRulesFeatureName = 'explicitSaveOfferRules';
const legacyIpopRoute = '/settings/ipop';
const privateOffersTab = 'privateOffers';
const offerSetRulesTab = 'offerSetRules';
const vdpOffersTab = 'vdpOffers';
const allowedTabs = [privateOffersTab, offerSetRulesTab, vdpOffersTab];

@Injectable({
  providedIn: 'root'
})
export class IpopExplicitSaveFeatureGuard
{
  constructor(
    private userService: UserService,
    private router: Router
  )
  {
  }

  /**
   * Allows the explicit-save route when enabled and redirects to the legacy route otherwise.
   */
  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree>
  {
    const featureRequest = this.userService.getMpopFeature(explicitSaveOfferRulesFeatureName).pipe(
      map((features: MpopFeatureModel[]) => this.resolveRoute(features, route)),
      catchError(() => of(this.buildLegacyRoute(route)))
    );

    return featureRequest;
  }

  /**
   * Resolves the route decision from the feature response.
   */
  private resolveRoute(features: MpopFeatureModel[], route: ActivatedRouteSnapshot): boolean | UrlTree
  {
    let routeDecision: boolean | UrlTree = this.buildLegacyRoute(route);

    if (this.isFeatureEnabled(features)) {
      routeDecision = true;
    }

    return routeDecision;
  }

  /**
   * Verifies that the feature response contains one enabled explicit-save flag.
   */
  private isFeatureEnabled(features: MpopFeatureModel[]): boolean
  {
    let isEnabled = false;

    if (features.length === 1
      && features[0].name === explicitSaveOfferRulesFeatureName
      && features[0].enabled === true) {
      isEnabled = true;
    }

    return isEnabled;
  }

  /**
   * Builds the legacy IPOP route while preserving supported query parameters.
   */
  private buildLegacyRoute(route: ActivatedRouteSnapshot): UrlTree
  {
    let accountId = '';
    const routeAccountId = route.paramMap.get('accountId');

    if (routeAccountId !== null) {
      accountId = routeAccountId;
    }

    const legacyRoute = this.router.createUrlTree(
      [legacyIpopRoute, accountId],
      {queryParams: this.buildAllowedQueryParams(route)}
    );

    return legacyRoute;
  }

  /**
   * Copies only supported tab selections to the legacy route.
   */
  private buildAllowedQueryParams(route: ActivatedRouteSnapshot): Params
  {
    const queryParams: Params = {};
    const requestedTab = route.queryParamMap.get('tab');

    if (requestedTab !== null && allowedTabs.includes(requestedTab)) {
      queryParams.tab = requestedTab;
    }

    return queryParams;
  }
}
